import { Google } from '@mui/icons-material';
import { Box, Button, CircularProgress, SvgIcon } from '@mui/material';
import { calendarApiV3 as api } from 'api';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    checkSyncStatusAction,
    disableCalendarIntegrationAction,
    fetchCalendarIntegrationAction,
    fetchHasCredentialAction,
    fetchHasSyncAction,
    getCredentialLevel,
    getHasCredential,
    getIsConnected,
    getPendingHasCredential,
    getPendingIntegration,
    getPendingSync,
    getProviderType,
    syncCalendarAction,
} from 'redux/slices/icalendarProviderSlice';
import ICalendarIntegrationDialog from './ICalendarIntegrationDialog';
import useStylesSettings from '../styles/useStylesSettings.styles';

const ICalendarIntegrationV3 = ({ organizationId }) => {
    const { t } = useTranslation();
    const { classes } = useStylesSettings();
    const dispatch = useDispatch();
    
    const isCalendarConnected = useSelector(getIsConnected);
    const integrationProviderType = useSelector(getProviderType);
    const integrationCredentialLevel = useSelector(getCredentialLevel);
    const pendingIntegration = useSelector(getPendingIntegration);
    const hasCredential = useSelector(getHasCredential);
    const pendingHasCredential = useSelector(getPendingHasCredential);
    const pendingSync = useSelector(getPendingSync);
    
    const [openIntegrationDialog, setOpenIntegrationDialog] = useState(false);

    const loadData = useCallback(() => {
        dispatch(fetchCalendarIntegrationAction(organizationId));
    }, [dispatch, organizationId]);

    useEffect(() => {
        loadData();
    }, [loadData]);
    
    // Cargar datos de credenciales
    const loadHasCredentialData = useCallback(() => {
        if (isCalendarConnected && integrationCredentialLevel === 'Organization') {
            dispatch(fetchHasCredentialAction({ organizationId }));
        }
    }, [dispatch, organizationId, isCalendarConnected, integrationCredentialLevel]);

    useEffect(() => {
        loadHasCredentialData();
    }, [loadHasCredentialData]);
    
    const handleCalendarIntegration = () => {
        if (isCalendarConnected) {
            dispatch(disableCalendarIntegrationAction(organizationId));
        } else {
            setOpenIntegrationDialog(true);
        }
    };
    
    const handleCloseIntegrationDialog = () => {
        setOpenIntegrationDialog(false);
    };
    
    const handleSyncCalendar = () => {
        dispatch(
            fetchHasSyncAction(
                { organizationId },
                (data) => {
                    dispatch(
                        syncCalendarAction({
                            organizationId,
                            subscribe: !data.hasSync,
                        })
                    );
                }
            )
        );
    };
    
    const handleAuthCalendarIntegration = () => {
        api.get(`integrations/${integrationProviderType.toLowerCase()}/auth-url?organizationId=${organizationId}`)
            .then((response) => {
                window.location.href = response.data.authUrl;
            })
            .catch((error) => {
                console.error(error);
            });
    };
    
    // Check sync status periodically
    const syncState = useCallback(() => {
        if (!pendingSync) return;
        dispatch(checkSyncStatusAction({ organizationId }));
    }, [dispatch, pendingSync, organizationId]);

    useEffect(() => {
        syncState();
        const intervalId = setInterval(syncState, 60000);
        return () => clearInterval(intervalId);
    }, [syncState]);
    
    const isAuthVisible = useCallback(() => {
        return (
            isCalendarConnected &&
            integrationCredentialLevel === 'Organization' &&
            !hasCredential &&
            !pendingHasCredential
        );
    }, [isCalendarConnected, integrationCredentialLevel, hasCredential, pendingHasCredential]);
    
    const isSyncVisible = useCallback(() => {
        return integrationCredentialLevel === 'Organization' && hasCredential;
    }, [integrationCredentialLevel, hasCredential]);
    
    return (
        <>
            <div className={classes.pb10px}>
                <Box display="flex" justifyContent="space-between" alignItems="center" style={{ width: '100%' }}>
                    <Box sx={{ display: 'inline-flex' }}>
                        <Button
                            variant="contained"
                            className={classes.redColor}
                            size="small"
                            onClick={handleCalendarIntegration}
                        >
                            {pendingIntegration ? (
                                <CircularProgress style={{ width: '80%', height: 'auto' }} color="secondary" />
                            ) : isCalendarConnected ? (
                                t('calendar.DisconnectFromExternalProvider')
                            ) : (
                                t('profile.settings.iCalendar.addCalendarIntegration')
                            )}
                        </Button>
                    </Box>
                </Box>
            </div>
            
            {isAuthVisible() && (
                <div className={classes.pb10px}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" style={{ width: '100%' }}>
                        <Box sx={{ display: 'inline-flex' }}>
                            <Button
                                variant="contained"
                                className={classes.redColor}
                                size="small"
                                onClick={handleAuthCalendarIntegration}
                            >
                                <div style={{ display: 'inline-flex', justifyContent: 'center', marginRight: '5px' }}>
                                    {integrationProviderType === 'Google' ? (
                                        <Google fontSize="small" />
                                    ) : (
                                        <SvgIcon color="gray">
                                            <path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V10.85l1.24.72h.01q.1.07.18.18.07.12.07.25zm-6-8.25v3h3v-3zm0 4.5v3h3v-3zm0 4.5v1.83l3.05-1.83zm-5.25-9v3h3.75v-3zm0 4.5v3h3.75v-3zm0 4.5v2.03l2.41 1.5 1.34-.8v-2.73zM9 3.75V6h2l.13.01.12.04v-2.3zM5.98 15.98q.9 0 1.6-.3.7-.32 1.19-.86.48-.55.73-1.28.25-.74.25-1.61 0-.83-.25-1.55-.24-.71-.71-1.24t-1.15-.83q-.68-.3-1.55-.3-.92 0-1.64.3-.71.3-1.2.85-.5.54-.75 1.3-.25.74-.25 1.63 0 .85.26 1.56.26.72.74 1.23.48.52 1.17.81.69.3 1.56.3zM7.5 21h12.39L12 16.08V17q0 .41-.3.7-.29.3-.7.3H7.5zm15-.13v-7.24l-5.9 3.54Z" />
                                        </SvgIcon>
                                    )}
                                </div>
                                {t('profile.settings.iCalendar.icalendarAuthAction')}
                            </Button>
                        </Box>
                    </Box>
                </div>
            )}
            
            {isSyncVisible() && (
                <div className={classes.pb10px}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" style={{ width: '100%' }}>
                        <Box sx={{ display: 'inline-flex' }}>
                            <Button
                                variant="contained"
                                className={classes.redColor}
                                size="small"
                                onClick={handleSyncCalendar}
                                disabled={pendingSync}
                            >
                                {pendingSync ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    t('profile.settings.iCalendar.syncCalendars')
                                )}
                            </Button>
                        </Box>
                    </Box>
                </div>
            )}
            
            <ICalendarIntegrationDialog
                openDialog={openIntegrationDialog}
                onCloseDialog={handleCloseIntegrationDialog}
            />
        </>
    );
};

export default ICalendarIntegrationV3;